import axios from "axios";
import { API_BASE_URL, APP_VERSION } from "../config/config";
import { STORAGE_KEYS } from "../constants/storage";
import {
  getSessionId,
  generateRequestId,
  clearSessionId
} from "../utils/sessionManager";

/**
 * Centralized API Client
 * Handles authentication, request/response interceptors, and common configurations
 */

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json"
  }
});

// Request interceptor - Add auth token and security headers to all requests
apiClient.interceptors.request.use(
  (config) => {
    // 1. Add Authorization token
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 2. Add X-Session-Id (unique per browser tab)
    const sessionId = getSessionId();
    config.headers["X-Session-Id"] = sessionId;

    // 3. Add X-Request-ID (unique per API call for tracking)
    const requestId = generateRequestId();
    config.headers["X-Request-ID"] = requestId;

    // 4. Add X-Organization-ID (from user details if available)
    const userDetails = localStorage.getItem(STORAGE_KEYS.USER_DETAILS);
    if (userDetails) {
      try {
        const parsedDetails = JSON.parse(userDetails);
        if (parsedDetails.organization_id) {
          config.headers["X-Organization-ID"] = parsedDetails.organization_id;
        }
      } catch (e) {
        // Silently fail if user details parsing fails
      }
    }

    // 5. Add X-Client-Version (app version from config)
    config.headers["X-Client-Version"] = APP_VERSION;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 - Unauthorized (token expired or invalid)
    if (error.response?.status === 401) {
      // Clear auth data
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.USER_DETAILS);
      // Clear session ID
      clearSessionId();

      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Standardized error handler
 * Extracts meaningful error messages from API responses
 */
export const handleApiError = (error, defaultMessage = "An error occurred") => {
  console.error("API Error:", error);

  let errorMessage = defaultMessage;

  if (error.response) {
    const status = error.response.status;
    const errorData = error.response.data;

    // Handle specific status codes
    if (status === 401) {
      errorMessage = errorData?.detail || "Authentication failed";
    } else if (status === 403) {
      errorMessage = errorData?.detail || "Access denied";
    } else if (status === 404) {
      errorMessage = errorData?.detail || "Resource not found";
    } else if (status === 422) {
      // For validation errors, try to extract field-specific errors
      if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail
          .map((e) => `${e.loc?.join(".")}: ${e.msg}`)
          .join("; ");
      } else {
        errorMessage = errorData?.detail || "Invalid input data";
      }
    } else if (status >= 500) {
      errorMessage =
        errorData?.detail ||
        errorData?.message ||
        "Server error. Please try again later";
    } else if (errorData?.detail) {
      errorMessage = errorData.detail;
    }
  } else if (error.request) {
    errorMessage = "Network error. Please check your connection";
  } else if (error.code === "ECONNABORTED") {
    errorMessage = "Request timeout. Please try again";
  } else if (error.message) {
    errorMessage = error.message;
  }

  return {
    success: false,
    message: errorMessage
  };
};

/**
 * Wrapper for API calls with standardized response format
 *
 * @param {Function} apiCall - The async API call function
 * @param {string} errorMessage - Default error message
 * @returns {Promise<{success: boolean, data?: any, message?: string}>}
 */
export const apiRequest = async (apiCall, errorMessage) => {
  try {
    const response = await apiCall();
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};

export default apiClient;
